import { useEffect } from "react"
import { useForm, type Resolver } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { useQuery, useMutation } from "urql"
import { toast } from "sonner"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Loader2 } from "lucide-react"
import { FieldError } from "@/components/shared/field-error"
import { streamSchema, type StreamFormValues } from "@/lib/schemas"
import { CreateStreamMutation, UpdateStreamMutation } from "@/lib/graphql/streaming"
import { ApplicationsQuery } from "@/lib/graphql/outbound"
import { useRetentionCap } from "@/hooks/use-session"

interface StreamFormProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSuccess: () => void
  initialData?: {
    id: string
    applicationId: string
    name: string
    status: string
    retentionDays: number
  }
}

export function StreamForm({ open, onOpenChange, onSuccess, initialData }: StreamFormProps) {
  const isEdit = !!initialData
  const [, create] = useMutation(CreateStreamMutation)
  const [, update] = useMutation(UpdateStreamMutation)
  const [appsResult] = useQuery({ query: ApplicationsQuery, variables: { limit: 100 } })
  const { maxDays, unlimited } = useRetentionCap()
  const defaultRetention = unlimited ? 30 : Math.min(30, maxDays)
  const clampRetention = (v: number) => (unlimited ? v : Math.min(Math.max(v, 1), maxDays))

  const form = useForm<StreamFormValues>({
    resolver: zodResolver(streamSchema) as Resolver<StreamFormValues>,
    defaultValues: {
      applicationId: initialData?.applicationId ?? "",
      name: initialData?.name ?? "",
      retentionDays: initialData?.retentionDays ?? defaultRetention,
      status: (initialData?.status as "ACTIVE" | "PAUSED") ?? "ACTIVE",
    },
  })

  useEffect(() => {
    if (open) {
      form.reset({
        applicationId: initialData?.applicationId ?? "",
        name: initialData?.name ?? "",
        retentionDays: initialData?.retentionDays ?? defaultRetention,
        status: (initialData?.status as "ACTIVE" | "PAUSED") ?? "ACTIVE",
      })
    }
  }, [open, initialData, form, defaultRetention])

  const onSubmit = async (values: StreamFormValues) => {
    const retentionDays = clampRetention(values.retentionDays ?? defaultRetention)
    if (isEdit) {
      const result = await update({
        id: initialData.id,
        input: {
          name: values.name,
          status: values.status as "ACTIVE" | "PAUSED",
          retentionDays,
        },
      })
      if (result.error) { toast.error(result.error.message); return }
      toast.success("Stream updated")
    } else {
      const result = await create({
        input: {
          applicationId: values.applicationId,
          name: values.name,
          retentionDays,
        },
      })
      if (result.error) { toast.error(result.error.message); return }
      toast.success("Stream created")
    }

    onSuccess()
    onOpenChange(false)
    form.reset()
  }

  const apps = appsResult.data?.applications.nodes ?? []

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit Stream" : "Create Stream"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          <div className="space-y-4 rounded-lg glass-card p-4">
            {!isEdit && (
              <div className="space-y-2">
                <Label htmlFor="applicationId">Application</Label>
                <Select
                  value={form.watch("applicationId")}
                  onValueChange={(v) => form.setValue("applicationId", v)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select application" />
                  </SelectTrigger>
                  <SelectContent>
                    {apps.map((app) => (
                      <SelectItem key={app.id} value={app.id}>{app.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FieldError error={form.formState.errors.applicationId} />
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="name">Name</Label>
              <Input id="name" {...form.register("name")} />
              <FieldError error={form.formState.errors.name} />
            </div>

            <div className="space-y-2">
              <Label htmlFor="retentionDays">Retention (days)</Label>
              <Input
                id="retentionDays"
                type="number"
                min={1}
                max={unlimited ? undefined : maxDays}
                {...form.register("retentionDays")}
              />
              {!unlimited && (
                <p className="text-xs text-muted-foreground/70">
                  Your plan allows up to {maxDays} days.
                </p>
              )}
              <FieldError error={form.formState.errors.retentionDays} />
            </div>

            {isEdit && (
              <div className="space-y-2">
                <Label>Status</Label>
                <Select
                  value={form.watch("status")}
                  onValueChange={(v) => form.setValue("status", v as "ACTIVE" | "PAUSED")}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ACTIVE">Active</SelectItem>
                    <SelectItem value="PAUSED">Paused</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={form.formState.isSubmitting}>
              {form.formState.isSubmitting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : isEdit ? "Update" : "Create"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}
